// Pages/Home/Dashboard.jsx - Fixed version with improved date handling and error prevention
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../translations/translations';
import {
    Users, CreditCard, CheckSquare,
    Calendar, RefreshCw, AlertTriangle, Mail, MessageSquare,
    X, ChevronLeft, Clock, MapPin, User, Phone
} from 'lucide-react';
import { sendWhatsAppReminder } from '../../Fetch/fetchWhatsAppNotification';
import { ChevronDown, BarChart3, FileText } from 'lucide-react';
import styles from './Dashboard.module.css';

// Import fetch functions
import { getUsersByUserId, getListUsers } from '../../Fetch/fetchUser';
import { getTasks } from '../../Fetch/fetchTasks';
import { listEvents } from '../../Fetch/fetchEvent';
import { getListPaymentsByBuildingId, getVaadMonthlySimple } from '../../Fetch/fetchPayment';
import { getExpenses } from '../../Fetch/fetchExpenses';
import { sendPaymentReminderEmail } from '../../Fetch/fetchSendEmailNotification';
import { getLatestBank } from '../../Fetch/fetchBank';

import { useRecurringExpenses } from '../../hooks/useRecurringExpenses';
import RecurringExpensesPopup from '../../Pages/Expenses/RecurringExpensesPopup';


// Import whatsapp functions
import { sendWhatsAppMessage, createDebtMessage, createSummaryReport, createSummaryReportWithPhones } from '../../utils/whatsappUtils';

// Import backup functions
import { listBackups } from '../../Fetch/fetchBackup';

// Utility function for safe date parsing
const safeParseDate = (dateValue, fallback = null) => {
    if (!dateValue) return fallback;
    
    // Handle different date formats
    const date = new Date(dateValue);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
        return fallback;
    }
    
    return date;
};

// Utility function for safe date formatting
const safeDateFormat = (dateValue, locale = 'he-IL', options = {}, t = null) => {
    const date = safeParseDate(dateValue);
    const fallback = t ? t('notAvailableShort') : 'לא זמין';
    if (!date) return fallback;
    
    try {
        return date.toLocaleDateString(locale, options);
    } catch (error) {
        return fallback;
    }
};

// You'll need to create this fetch function for building info
const getBuildingInfo = async (buildingId) => {
    try {
        const token = localStorage.getItem('token');
        const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';

        const response = await fetch(`${API_URL}/api/building/${buildingId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Get building info failed`);
        }

        const result = await response.json();
        return result;
    } catch (err) {
        return null;
    }
};

// Modal Component for Unpaid Apartment Details
const UnpaidDetailsModal = ({ apartmentData, isOpen, onClose, onSendReminder, buildingData }) => {
    const [isSendingEmailReminder, setIsSendingEmailReminder] = useState(false);
    const [isSendingWhatsAppReminder, setIsSendingWhatsAppReminder] = useState(false);
    const [reminderStatus, setReminderStatus] = useState(null);

    if (!isOpen || !apartmentData) return null;

    // Handle Email Reminder (works with your existing structure)
    const handleSendEmailReminder = async () => {
        const usersWithEmail = apartmentData.users.filter(user => user.email);

        if (usersWithEmail.length === 0) {
            setReminderStatus({
                type: 'error',
                message: 'אין כתובות אימייל זמינות לדיירי הדירה'
            });
            setTimeout(() => setReminderStatus(null), 3000);
            return;
        }

        setIsSendingEmailReminder(true);
        setReminderStatus(null);

        try {
            const results = [];

            for (const user of usersWithEmail) {
                const emailData = {
                    email: user.email,
                    name: user.name,
                    apartmentNumber: apartmentData.apartmentNumber,
                    buildingNumber: buildingData?.buildingNumber || '',
                    unpaidMonths: apartmentData.unpaidMonths || [],
                    monthsUnpaidCount: apartmentData.monthsUnpaidCount,
                    estimatedAmount: apartmentData.totalDebt || (apartmentData.vaadMonthly * apartmentData.monthsUnpaidCount)
                };

                const result = await sendPaymentReminderEmail(emailData);
                results.push(result);
            }

            const successCount = results.filter(result => result.success).length;

            if (successCount > 0) {
                setReminderStatus({
                    type: 'success',
                    message: `תזכורות אימייל נשלחו בהצלחה ל-${successCount} מתוך ${usersWithEmail.length} דיירים!`
                });
                if (onSendReminder) {
                    onSendReminder(apartmentData, 'success', successCount);
                }
            } else {
                setReminderStatus({
                    type: 'error',
                    message: 'שגיאה בשליחת תזכורות אימייל'
                });
            }
        } catch (error) {
            setReminderStatus({
                type: 'error',
                message: 'שגיאה בשליחת תזכורות אימייל'
            });
        } finally {
            setIsSendingEmailReminder(false);
            setTimeout(() => setReminderStatus(null), 5000);
        }
    };

    // Handle WhatsApp Reminder
    const handleSendWhatsAppReminder = async () => {
        const usersWithPhone = apartmentData.users.filter(user => user.phone);

        if (usersWithPhone.length === 0) {
            setReminderStatus({
                type: 'error',
                message: 'אין מספרי טלפון זמינים לשליחת WhatsApp'
            });
            setTimeout(() => setReminderStatus(null), 3000);
            return;
        }

        setIsSendingWhatsAppReminder(true);
        setReminderStatus(null);

        try {
            const whatsappData = {
                apartmentNumber: apartmentData.apartmentNumber,
                buildingNumber: buildingData?.buildingNumber || '',
                users: usersWithPhone.map(user => ({
                    name: user.name,
                    phone: user.phone
                })),
                unpaidMonths: apartmentData.unpaidMonths || [],
                monthsUnpaidCount: apartmentData.monthsUnpaidCount,
                totalDebt: apartmentData.totalDebt || (apartmentData.vaadMonthly * apartmentData.monthsUnpaidCount),
                vaadMonthly: apartmentData.vaadMonthly
            };

            const result = await sendWhatsAppReminder(whatsappData);

            if (result.success) {
                const successCount = result.data?.summary?.successful || usersWithPhone.length;
                setReminderStatus({
                    type: 'success',
                    message: `תזכורות WhatsApp נשלחו בהצלחה ל-${successCount} מתוך ${usersWithPhone.length} דיירים!`
                });
                if (onSendReminder) {
                    onSendReminder(apartmentData, 'success', successCount);
                }
            } else {
                setReminderStatus({
                    type: 'error',
                    message: result.message || 'שגיאה בשליחת תזכורות WhatsApp'
                });
            }
        } catch (error) {
            setReminderStatus({
                type: 'error',
                message: 'שגיאה בשליחת תזכורות WhatsApp'
            });
        } finally {
            setIsSendingWhatsAppReminder(false);
            setTimeout(() => setReminderStatus(null), 5000);
        }
    };

    // WhatsApp Direct function for individual apartment
    const handleWhatsAppDirect = () => {
        // Create the debt message using the utility function
        const message = createDebtMessage(apartmentData, buildingData);

        // Get users with phone numbers
        const usersWithPhone = apartmentData.users.filter(user => user.phone && user.phone.trim() !== '');

        let phoneNumber = null;
        let successMessage;

        // Use first available phone number if exists
        if (usersWithPhone.length > 0) {
            const targetUser = usersWithPhone[0];
            phoneNumber = targetUser.phone;
            successMessage = `📱 WhatsApp נפתח לשליחה ל${targetUser.name} (${targetUser.phone})`;

            if (usersWithPhone.length > 1) {
                successMessage += ` (+${usersWithPhone.length - 1} דיירים נוספים עם טלפון)`;
            }
        } else {
            successMessage = `📱 WhatsApp נפתח - בחר איש קשר לשליחה (אין מספרי טלפון במערכת)`;
        }

        // Send the WhatsApp message
        const whatsappTab = sendWhatsAppMessage(message, phoneNumber);

        // Show result to user
        if (!whatsappTab) {
            setReminderStatus({
                type: 'error',
                message: '❌ חלון WhatsApp נחסם על ידי הדפדפן. אנא אשר חלונות קופצים.'
            });
        } else {
            setReminderStatus({
                type: 'success',
                message: successMessage
            });
        }

        setTimeout(() => setReminderStatus(null), 5000);
    };

    const usersWithEmail = apartmentData.users.filter(user => user.email);
    const usersWithPhone = apartmentData.users.filter(user => user.phone);

    const getBuildingAgeInMonths = () => {
        if (!buildingData?.data?.createDate && !buildingData?.createDate && !buildingData?.CreatedDate) {
            return apartmentData.monthsUnpaidCount;
        }

        const buildingCreateDate = buildingData?.data?.createDate || buildingData?.createDate || buildingData?.CreatedDate;
        const currentDate = new Date();
        const createDate = safeParseDate(buildingCreateDate);

        if (!createDate) {
            return apartmentData.monthsUnpaidCount;
        }

        const monthsDiff = Math.ceil((currentDate - createDate) / (1000 * 60 * 60 * 24 * 30));
        return Math.max(1, monthsDiff);
    };

    const buildingAgeMonths = getBuildingAgeInMonths();
    const buildingCreateDate = buildingData?.data?.createDate || buildingData?.createDate || buildingData?.CreatedDate;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3 className={styles.modalTitle}>
                        פירוט חובות - דירה {apartmentData.apartmentNumber}
                    </h3>
                    <button className={styles.modalCloseButton} onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className={styles.modalBody}>
                    {buildingCreateDate && buildingCreateDate !== 'לא זמין' && (
                        <div className={styles.buildingInfo}>
                            <h4>מידע על הבניין:</h4>
                            <p>📅 תאריך הקמת בניין: {safeDateFormat(buildingCreateDate)}</p>
                        </div>
                    )}

                    <div className={styles.apartmentDetails}>
                        <h4>דיירי הדירה ({apartmentData.usersCount} דיירים):</h4>
                        <div className={styles.usersGrid}>
                            {apartmentData.users.map((user, index) => (
                                <div key={user.id || index} className={styles.userItem}>
                                    <div className={styles.userInfo}>
                                        <div className={styles.userName}>{user.name}</div>
                                        <div className={styles.userContact}>
                                            {user.phone && (
                                                <span className={styles.whatsappNumber}>
                                                    📞 {user.phone}
                                                </span>
                                            )}
                                            {user.email && <span>📧 {user.email}</span>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.unpaidMonthsSection}>
                        <h4>חודשים שלא שולמו ({apartmentData.monthsUnpaidCount} מתוך {buildingAgeMonths} חודשים):</h4>
                        <div className={styles.unpaidMonthsGrid}>
                            {apartmentData.unpaidMonths && apartmentData.unpaidMonths.length > 0 ? (
                                apartmentData.unpaidMonths.map((month, index) => (
                                    <div key={index} className={styles.unpaidMonthItem}>
                                        <Calendar size={16} />
                                        <span>{month}</span>
                                    </div>
                                ))
                            ) : (
                                <p>אין נתונים על חודשים לא שולמו</p>
                            )}
                        </div>
                    </div>

                    <div className={styles.debtSummary}>
                        <div className={styles.debtItem} style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>
                            <span>דמי ועד חודשיים:</span>
                            <span>₪{(apartmentData.vaadMonthly || 500).toLocaleString()}</span>
                        </div>
                        <div className={styles.debtItem}>
                            <span className={styles.debtLabel}>סכום חוב משוער:</span>
                            <span className={styles.debtAmount}>₪{(apartmentData.monthsUnpaidCount * (apartmentData.vaadMonthly || 500)).toLocaleString()}</span>
                        </div>
                        <div className={styles.debtItem} style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem' }}>
                            <span>אחוז חוב:</span>
                            <span>{Math.round((apartmentData.monthsUnpaidCount / buildingAgeMonths) * 100)}% מתקופת קיום הבניין</span>
                        </div>
                    </div>

                    <div className={styles.contactSummary} style={{
                        background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                        padding: '1rem',
                        borderRadius: '12px',
                        marginTop: '1rem'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                            <span>📧 דיירים עם אימייל: {usersWithEmail.length}</span>
                            <span>📱 דיירים עם WhatsApp: {usersWithPhone.length}</span>
                        </div>
                    </div>

                    {reminderStatus && (
                        <div className={`${styles.reminderStatus} ${reminderStatus.type === 'success' ? styles.reminderSuccess : styles.reminderError}`}>
                            {reminderStatus.type === 'success' ? '✅' : '❌'} {reminderStatus.message}
                        </div>
                    )}
                </div>

                <div className={styles.modalFooter}>
                    <button className={`${styles.modalButton} ${styles.modalButtonPrimary}`} onClick={onClose}>
                        סגור
                    </button>

                    <button
                        className={`${styles.modalButton} ${styles.modalButtonSecondary} ${usersWithEmail.length === 0 ? 'disabled' : ''}`}
                        onClick={handleSendEmailReminder}
                        disabled={isSendingEmailReminder || usersWithEmail.length === 0}
                        title={usersWithEmail.length === 0 ? 'אין כתובות אימייל' : `שלח אימייל ל-${usersWithEmail.length} דיירים`}
                    >
                        {isSendingEmailReminder ? (
                            <RefreshCw size={16} className={styles.rotating} />
                        ) : (
                            <Mail size={16} />
                        )}
                    </button>

                    <button
                        className={`${styles.modalButton} ${styles.whatsappDirectButton}`}
                        onClick={handleWhatsAppDirect}
                        title="פתח WhatsApp עם הודעת חוב מפורטת"
                    >
                        <MessageSquare size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Card Components for Mobile
const TaskCard = ({ description, status, date, taskUser, getStatusClass, onDoubleClick }) => (
    <div className={styles.dataCard} onDoubleClick={onDoubleClick} style={{ cursor: 'pointer' }}>
        <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>
                <CheckSquare size={16} className={styles.cardIcon} />
                <span>{description}</span>
            </div>
            <span className={`${styles.statusBadge} ${getStatusClass(status)}`}>
                {status}
            </span>
        </div>
        <div className={styles.cardBody}>
            <div className={styles.cardInfo}>
                <Calendar size={14} />
                <span>תאריך יעד: {date}</span>
            </div>
            <div className={styles.cardInfo}>
                <User size={14} />
                <span>אחראי: {taskUser}</span>
            </div>
        </div>
        <div className={styles.doubleClickHint}>
            לחץ פעמיים לעריכה
        </div>
    </div>
);

const EventCard = ({ name, date, time, location, onDoubleClick }) => (
    <div className={styles.dataCard} onDoubleClick={onDoubleClick} style={{ cursor: 'pointer' }}>
        <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>
                <Calendar size={16} className={styles.cardIcon} />
                <span>{name}</span>
            </div>
        </div>
        <div className={styles.cardBody}>
            <div className={styles.cardInfo}>
                <Calendar size={14} />
                <span>תאריך: {date}</span>
            </div>
            <div className={styles.cardInfo}>
                <Clock size={14} />
                <span>שעה: {time}</span>
            </div>
            <div className={styles.cardInfo}>
                <MapPin size={14} />
                <span>מיקום: {location}</span>
            </div>
        </div>
        <div className={styles.doubleClickHint}>
            לחץ פעמיים לעריכה
        </div>
    </div>
);

const UnpaidApartmentCard = ({
    apartmentNumber,
    users,
    usersCount,
    monthsUnpaidCount,
    buildingCreateDate,
    getUnpaidPriorityClass,
    getUnpaidPriorityText,
    onClick
}) => (
    <div className={`${styles.dataCard} ${styles.unpaidUserCard}`} onClick={onClick}>
        <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>
                <AlertTriangle size={16} className={styles.cardIcon} />
                <span>דירה {apartmentNumber}</span>
            </div>
            <span className={`${styles.priorityBadge} ${getUnpaidPriorityClass(monthsUnpaidCount)}`}>
                {getUnpaidPriorityText(monthsUnpaidCount)}
            </span>
        </div>
        <div className={styles.cardBody}>
            <div className={styles.cardInfo}>
                <User size={14} />
                <span>{usersCount} דיירים</span>
            </div>
            <div className={styles.cardInfo}>
                <User size={14} />
                <span>דייר ראשי: {users[0]?.name}</span>
            </div>
            {users[0]?.phone && (
                <div className={styles.cardInfo}>
                    <Phone size={14} />
                    <span>טלפון: {users[0].phone}</span>
                </div>
            )}
            {users[0]?.email && (
                <div className={styles.cardInfo}>
                    <Mail size={14} />
                    <span>{users[0].email}</span>
                </div>
            )}
        </div>
    </div>
);

// Custom hook for counting animation
const useCountAnimation = (end, duration = 1000) => {
    const [count, setCount] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);

    useEffect(() => {
        if (end === 0 || hasAnimated) {
            setCount(end);
            return;
        }

        setHasAnimated(true);
        const startTime = Date.now();
        const startValue = 0;

        const animate = () => {
            const now = Date.now();
            const progress = Math.min((now - startTime) / duration, 1);

            // Easing function for smooth animation
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentCount = Math.floor(startValue + (end - startValue) * easeOutQuart);

            setCount(currentCount);

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                setCount(end);
            }
        };

        requestAnimationFrame(animate);
    }, [end, duration, hasAnimated]);

    return count;
};

// Stat Cards Component with counting animation
const StatCards = ({ usersLength, currentMonthIncome, currentMonthExpenses, currentMonthNetProfit, tasksCount, isDashboardLoading, t }) => {
    const animatedUsersLength = useCountAnimation(usersLength, 800);
    const animatedIncome = useCountAnimation(currentMonthIncome, 1000);
    const animatedExpenses = useCountAnimation(currentMonthExpenses, 1000);
    const animatedNetProfit = useCountAnimation(currentMonthNetProfit, 1200);
    const animatedTasksCount = useCountAnimation(tasksCount, 900);

    return (
        <div className={`${styles.statCards} ${isDashboardLoading ? styles.loading : ''}`}>
            <div className={`${styles.statCard} ${styles.statIconBlue}`}>
                <div className={styles.statContent}>
                    <p className={styles.statLabel}>{t('totalResidents')}</p>
                    <p className={styles.statValue}>{animatedUsersLength}</p>
                </div>
                <div className={styles.statIcon}>
                    <Users size={28} className={styles.iconBlue} />
                </div>
            </div>

            <div className={`${styles.statCard} ${styles.statIconGreen}`}>
                <div className={styles.statContent}>
                    <p className={styles.statLabel}>{t('thisMonthIncome')}</p>
                    <p className={styles.statValue}>₪{animatedIncome.toLocaleString()}</p>
                </div>
                <div className={styles.statIcon}>
                    <span style={{fontSize: '32px', color: '#10b981'}}>₪</span>
                </div>
            </div>

            <div className={`${styles.statCard} ${styles.statIconRed}`}>
                <div className={styles.statContent}>
                    <p className={styles.statLabel}>{t('thisMonthExpenses')}</p>
                    <p className={styles.statValue}>₪{animatedExpenses.toLocaleString()}</p>
                </div>
                <div className={styles.statIcon}>
                    <CreditCard size={28} className={styles.iconRed} />
                </div>
            </div>

            <div className={`${styles.statCard} ${currentMonthNetProfit >= 0 ? styles.statIconGreen : styles.statIconRed}`}>
                <div className={styles.statContent}>
                    <p className={styles.statLabel}>{t('netProfit')}</p>
                    <p className={styles.statValue}>₪{animatedNetProfit.toLocaleString()}</p>
                </div>
                <div className={styles.statIcon}>
                    <CreditCard size={28} className={currentMonthNetProfit >= 0 ? styles.iconGreen : styles.iconRed} />
                </div>
            </div>

            <div className={`${styles.statCard} ${styles.statIconPurple}`}>
                <div className={styles.statContent}>
                    <p className={styles.statLabel}>{t('upcomingTasks')}</p>
                    <p className={styles.statValue}>{animatedTasksCount}</p>
                </div>
                <div className={styles.statIcon}>
                    <CheckSquare size={28} className={styles.iconPurple} />
                </div>
            </div>
        </div>
    );
};

export default function Dashboard({ userData }) {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const { t } = useTranslation(language);

    // Dashboard-specific state
    const [usersData, setUsersData] = useState([]);
    const [tasksData, setTasksData] = useState([]);
    const [eventsData, setEventsData] = useState([]);
    const [paymentsData, setPaymentsData] = useState([]);
    const [expensesData, setExpensesData] = useState([]);
    const [users, setUsers] = useState([]);
    const [unpaidApartments, setUnpaidApartments] = useState([]);
    const [usersLength, setUsersLength] = useState(0);
    const [buildingData, setBuildingData] = useState(null);
    const [buildingAddress, setBuildingAddress] = useState(null);
    const [vaadMonthlyData, setVaadMonthlyData] = useState({});
    const [currentMonthIncome, setCurrentMonthIncome] = useState(0);
    const [currentMonthExpenses, setCurrentMonthExpenses] = useState(0);
    const [currentMonthNetProfit, setCurrentMonthNetProfit] = useState(0);
    const [currentBankBalance, setCurrentBankBalance] = useState(null);

    // Loading and modal states
    const [isDashboardLoading, setIsDashboardLoading] = useState(false);
    const [selectedUnpaidApartment, setSelectedUnpaidApartment] = useState(null);
    const [isUnpaidModalOpen, setIsUnpaidModalOpen] = useState(false);
    const [reminderNotification, setReminderNotification] = useState(null);

    const [isWhatsappDropdownOpen, setIsWhatsappDropdownOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    // Backup list state (for admin user only)
    const [recentBackups, setRecentBackups] = useState([]);
    const [isBackupsLoading, setIsBackupsLoading] = useState(false);
    const [showBackupModal, setShowBackupModal] = useState(false);

    // Add recurring expenses hook
    const {
        recurringExpenses,
        isLoading: isRecurringLoading,
        error: recurringError,
        showPopup: showRecurringPopup,
        closePopup: closeRecurringPopup,
        handleExpensesCreated: handleRecurringExpensesCreated,
        refreshRecurringExpenses,
        showPopupManually // For manual testing
    } = useRecurringExpenses(userData?.buildingId, true);

    // Add this function to handle recurring expenses creation
    const handleRecurringExpenseCreated = (createdExpenses) => {
        // Handle the created expenses
        handleRecurringExpensesCreated(createdExpenses);
        
        // Refresh dashboard data to show the new expenses
        refreshDashboardData();
        
        // Show success notification
        setReminderNotification({
            type: 'success',
            message: `נוצרו ${createdExpenses.length} הוצאות חוזרות בהצלחה`
        });
        
        setTimeout(() => setReminderNotification(null), 5000);
    };

    const handleTestRecurringExpenses = () => {
        if (recurringExpenses.length > 0) {
            showPopupManually();
        } else {
            // Force refresh to check for new recurring expenses
            refreshRecurringExpenses(true);
        }
    };

    // Double-click handlers
    const handleTaskDoubleClick = (task) => {

        navigate('/app/tasks', {
            state: {
                editingTask: task,
                fromNotifications: true
            }
        });
    };

    const handleEventDoubleClick = (event) => {

        navigate('/app/events', {
            state: {
                editingEvent: event,
                fromDashboard: true
            }
        });
    };

    // Function to calculate current month's income from payments - FIXED
    const calculateCurrentMonthIncome = (payments) => {
        if (!payments || !Array.isArray(payments)) return 0;

        const currentDate = new Date();
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();

        const currentMonthPayments = payments.filter(payment => {
            const paymentDate = safeParseDate(payment.Date || payment.date);
            if (!paymentDate) return false;
            
            return paymentDate.getMonth() === currentMonth &&
                paymentDate.getFullYear() === currentYear;
        });

        return currentMonthPayments.reduce((total, payment) => {
            return total + (payment.Amount || payment.amount || 0);
        }, 0);
    };

    // Function to calculate current month's expenses - FIXED
    const calculateCurrentMonthExpenses = (expenses) => {
        if (!expenses || !Array.isArray(expenses)) return 0;

        const currentDate = new Date();
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();

        const currentMonthExpenses = expenses.filter(expense => {
            const expenseDate = safeParseDate(expense.Date || expense.date);
            if (!expenseDate) return false;
            
            return expenseDate.getMonth() === currentMonth &&
                expenseDate.getFullYear() === currentYear;
        });

        return currentMonthExpenses.reduce((total, expense) => {
            return total + (expense.Amount || expense.amount || 0);
        }, 0);
    };

    // Function to calculate unpaid apartments (grouping users by apartment) - FIXED
    const calculateUnpaidApartments = (users, payments, buildingCreateDate = null, vaadMonthlyMap = {}) => {

        if (!users || !Array.isArray(users)) {
            return [];
        }

        if (!payments || !Array.isArray(payments)) {
            payments = [];
        }

        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear();

        // Calculate building start date - FIXED
        let paymentStartDate;

        if (buildingCreateDate && buildingCreateDate !== 'לא זמין' && buildingCreateDate !== null) {
            const testDate = safeParseDate(buildingCreateDate);
            
            if (testDate && testDate <= currentDate) {
                paymentStartDate = new Date(testDate.getFullYear(), testDate.getMonth(), 1);
            } else {
                paymentStartDate = null;
            }
        } else {
            paymentStartDate = null;
        }

        if (!paymentStartDate) {
            const startOfYear = new Date(currentYear, 0, 1);
            const twelveMonthsAgo = new Date(currentYear, currentMonth - 12, 1);
            paymentStartDate = startOfYear > twelveMonthsAgo ? startOfYear : twelveMonthsAgo;
        }

        // Calculate months to check
        const monthsToCheck = [];
        let checkDate = new Date(paymentStartDate.getFullYear(), paymentStartDate.getMonth(), 1);
        const endDate = new Date(currentYear, currentMonth - 1, 1);


        let monthCount = 0;
        while (checkDate <= endDate && monthCount < 50) {
            const monthData = {
                month: checkDate.getMonth() + 1,
                year: checkDate.getFullYear(),
                monthName: checkDate.toLocaleDateString('he-IL', { month: 'short', year: 'numeric' }),
                monthKey: `${checkDate.getFullYear()}-${checkDate.getMonth() + 1}`
            };

            monthsToCheck.push(monthData);

            checkDate.setMonth(checkDate.getMonth() + 1);
            monthCount++;
        }


        if (monthsToCheck.length === 0) {
            monthsToCheck.push({
                month: currentMonth,
                year: currentYear,
                monthName: currentDate.toLocaleDateString('he-IL', { month: 'short', year: 'numeric' }),
                monthKey: `${currentYear}-${currentMonth}`
            });
        }

        // Group payments by apartment - FIXED
        // Filter only "ועד בית" payments, exclude "חד פעמי" payments
        const paymentsByApartment = {};

        payments.forEach((payment, index) => {
            try {
                // Get payment type and filter out one-time payments
                const paymentType = payment.paymentType || payment.PaymentType || '';
                const apartmentNumber = (
                    payment.apartmentNumber ||
                    payment.ApartmentNumber ||
                    payment.number ||
                    payment.Number
                )?.toString()?.trim();

                // Skip any payment that contains "חד פעמי" (handles both "חד פעמי" and "תשלום חד פעמי")
                if (paymentType.includes('חד פעמי')) {
                    return; // Skip one-time payments
                }

                const paymentDateValue = payment.date || payment.Date || payment.PaymentDate || payment.paymentDate;

                if (!paymentDateValue) {
                    return;
                }

                const paymentDate = safeParseDate(paymentDateValue);

                if (!paymentDate) {
                    return;
                }

                // Use the payment's 'month' and 'year' fields if available (for which month payment is FOR)
                // Otherwise fall back to extracting from payment date
                const paymentMonthField = payment.month || payment.Month;
                const paymentYearField = payment.year || payment.Year;

                const paymentMonth = paymentMonthField ? parseInt(paymentMonthField) : (paymentDate.getMonth() + 1);
                const paymentYear = paymentYearField ? parseInt(paymentYearField) : paymentDate.getFullYear();

                if (apartmentNumber && !isNaN(paymentMonth) && !isNaN(paymentYear)) {
                    if (!paymentsByApartment[apartmentNumber]) {
                        paymentsByApartment[apartmentNumber] = new Set();
                    }
                    const monthKey = `${paymentYear}-${paymentMonth}`;
                    paymentsByApartment[apartmentNumber].add(monthKey);
                }
            } catch (error) {
                console.error(`Error processing payment ${index}:`, error);
            }
        });


        // Group users by apartment
        const usersByApartment = {};

        users.forEach((user, index) => {
            try {
                // Try multiple apartment number field names
                const userApartment = (
                    user.apartmentNumber || 
                    user.ApartmentNumber || 
                    user.number ||
                    user.Number
                )?.toString()?.trim();


                if (userApartment) {
                    if (!usersByApartment[userApartment]) {
                        usersByApartment[userApartment] = [];
                    }
                    usersByApartment[userApartment].push(user);
                } else {
                    // User has no apartment number
                }
            } catch (error) {
                console.error(`❌ Error processing user ${index}:`, error);
            }
        });


        // Calculate unpaid apartments
        const unpaidApartments = [];

        Object.entries(usersByApartment).forEach(([apartmentNumber, apartmentUsers]) => {

            const apartmentPayments = paymentsByApartment[apartmentNumber] || new Set();

            const unpaidMonths = [];
            const paidMonths = [];

            monthsToCheck.forEach(monthData => {
                const monthKey = monthData.monthKey;

                if (apartmentPayments.has(monthKey)) {
                    paidMonths.push(monthData.monthName);
                } else {
                    unpaidMonths.push(monthData.monthName);
                }
            });

            const shouldIncludeApartment = unpaidMonths.length > 0 || apartmentPayments.size === 0;

            if (shouldIncludeApartment) {
                const vaadMonthly = vaadMonthlyMap[apartmentNumber] ||
                    apartmentUsers[0]?.vaadMonthly ||
                    apartmentUsers[0]?.VaadMonthly ||
                    500;

                const apartmentData = {
                    id: `apartment-${apartmentNumber}`,
                    apartmentNumber: apartmentNumber,
                    users: apartmentUsers.map(user => ({
                        id: user.userID,
                        name: user.fullName || user.FullName || 'לא זמין',
                        email: user.email || user.Email || '',
                        phone: user.phone || user.PhoneNumber || ''
                    })),
                    unpaidMonths: unpaidMonths,
                    paidMonths: paidMonths,
                    monthsUnpaidCount: unpaidMonths.length,
                    monthsPaidCount: paidMonths.length,
                    totalMonthsChecked: monthsToCheck.length,
                    buildingAgeMonths: monthsToCheck.length,
                    vaadMonthly: vaadMonthly,
                    totalDebt: unpaidMonths.length * vaadMonthly,
                    usersCount: apartmentUsers.length,
                    paymentStatus: unpaidMonths.length === monthsToCheck.length ? 'NO_PAYMENTS' : 'PARTIAL_PAYMENTS',
                    buildingCreateDateUsed: buildingCreateDate,
                    monthsCheckedList: monthsToCheck.map(m => m.monthName)
                };

                unpaidApartments.push(apartmentData);
            }
        });

        return unpaidApartments.sort((a, b) => {
            // Sort by apartment number (ascending)
            return parseInt(a.apartmentNumber) - parseInt(b.apartmentNumber);
        });
    };

    // Refresh dashboard data - ENHANCED ERROR HANDLING
    const refreshDashboardData = async () => {
        setIsDashboardLoading(true);
        try {
            const storedUser = localStorage.getItem('user');
            if (!storedUser) {
                return;
            }

            const user = JSON.parse(storedUser);
            const userId = user.userId;
            const buildingId = user.buildingId;

            setBuildingAddress(user.selectedBuildingInfo);

            if (!buildingId) {
                return;
            }

            // Use Promise.allSettled to handle partial failures
            const results = await Promise.allSettled([
                getUsersByUserId(userId),
                getTasks(buildingId),
                listEvents(buildingId),
                getListUsers(buildingId),
                getListPaymentsByBuildingId(buildingId),
                getExpenses(buildingId),
                getBuildingInfo(buildingId)
            ]);

            // Process results and handle failures gracefully
            const [usersResult, tasksResult, eventsResult, usersListResult, paymentsResult, expensesResult, buildingResult] = results;

            if (usersResult.status === 'fulfilled') setUsersData(usersResult.value || []);
            if (tasksResult.status === 'fulfilled') setTasksData(tasksResult.value || {});
            if (eventsResult.status === 'fulfilled') {
                // Filter out events with status "הסתיים" (completed)
                const activeEvents = (eventsResult.value || []).filter(event => event.status !== 'הסתיים');
                setEventsData(activeEvents);
            }
            if (usersListResult.status === 'fulfilled') {
                const usersList = usersListResult.value || [];
                setUsers(usersList);
                
                // Filter to count only tenants (IsTenant = "כן")
                const tenantsCount = usersList.filter(user => {
                    // Check if user is a tenant
                    const isTenant = user.IsTenant === "כן" || 
                                    user.isTenant === "כן";
                    return isTenant;
                }).length;

                setUsersLength(tenantsCount);
            }
            if (paymentsResult.status === 'fulfilled') setPaymentsData(paymentsResult.value || []);
            if (buildingResult.status === 'fulfilled') setBuildingData(buildingResult.value);

            // Handle VaadMonthly data separately with fallback
            let vaadMonthlyData = {};
            try {
                vaadMonthlyData = await getVaadMonthlySimple(buildingId);
            } catch (vaadError) {
                vaadMonthlyData = {};
            }
            setVaadMonthlyData(vaadMonthlyData || {});

            // Handle expenses data
            const expensesArray = expensesResult.status === 'fulfilled' ? 
                (expensesResult.value?.data || expensesResult.value || []) : [];
            setExpensesData(expensesArray);

            // Calculate financial data with safe methods
            const payments = paymentsResult.status === 'fulfilled' ? (paymentsResult.value || []) : [];
            const monthlyIncome = calculateCurrentMonthIncome(payments);
            const monthlyExpenses = calculateCurrentMonthExpenses(expensesArray);

            setCurrentMonthIncome(monthlyIncome);
            setCurrentMonthExpenses(monthlyExpenses);
            
            // Calculate net profit (income - expenses)
            const netProfit = monthlyIncome - monthlyExpenses;
            setCurrentMonthNetProfit(netProfit);

            // Calculate unpaid apartments
            if (usersListResult.status === 'fulfilled' && paymentsResult.status === 'fulfilled') {
                const usersList = usersListResult.value || [];
                const building = buildingResult.status === 'fulfilled' ? buildingResult.value : null;
                const buildingCreateDate = building?.data?.createDate || building?.createDate || building?.CreatedDate || null;
                const unpaid = calculateUnpaidApartments(usersList, payments, buildingCreateDate, vaadMonthlyData || {});
                setUnpaidApartments(unpaid);
            }

            // Fetch latest bank balance
            try {
                const latestBank = await getLatestBank(buildingId);
                if (latestBank && latestBank.moneyLeft !== undefined) {
                    setCurrentBankBalance(latestBank.moneyLeft);
                } else {
                    setCurrentBankBalance(null);
                }
            } catch (bankError) {
                setCurrentBankBalance(null);
            }

        } catch (error) {
        } finally {
            setIsDashboardLoading(false);
        }
    };

    useEffect(() => {
    }, [buildingAddress])

    useEffect(() => {
        refreshDashboardData();

        const handleBuildingChange = () => {
            refreshDashboardData();
        };

        window.addEventListener('buildingChanged', handleBuildingChange);
        return () => window.removeEventListener('buildingChanged', handleBuildingChange);
    }, []);

    // Fetch recent backups for admin user
    useEffect(() => {
        const fetchRecentBackups = async () => {
            if (userData?.email !== 'motisegev1@gmail.com') return;

            setIsBackupsLoading(true);
            try {
                const result = await listBackups();
                if (result?.backups) {
                    const sevenDaysAgo = new Date();
                    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

                    const recent = result.backups.filter(backup => {
                        const backupDate = new Date(backup.createdAt);
                        return backupDate >= sevenDaysAgo;
                    });
                    setRecentBackups(recent);
                    setShowBackupModal(true);
                }
            } catch (error) {
                console.error('Error fetching backups:', error);
            } finally {
                setIsBackupsLoading(false);
            }
        };

        fetchRecentBackups();
    }, [userData?.email]);

    // Handle unpaid apartment click
    const handleUnpaidApartmentClick = (apartment) => {
        setSelectedUnpaidApartment(apartment);
        setIsUnpaidModalOpen(true);
    };

    // Handle reminder sent
    const handleReminderSent = (apartmentData, status, count = 0) => {
        const message = status === 'success'
            ? `תזכורות נשלחו בהצלחה ל${count} דיירים בדירה ${apartmentData.apartmentNumber}`
            : `שגיאה בשליחת תזכורות לדירה ${apartmentData.apartmentNumber}`;

        setReminderNotification({ type: status, message });
        setTimeout(() => setReminderNotification(null), 5000);
    };

    // Send WhatsApp summary report
    const sendWhatsAppSummaryReport = async () => {
        if (!unpaidApartments || unpaidApartments.length === 0) {
            setReminderNotification({
                type: 'success',
                message: '🎉 כל הדיירים שילמו! אין חובות לדווח עליהם'
            });
            setTimeout(() => setReminderNotification(null), 3000);
            return;
        }

        try {
            // Extract bank data from buildingData for the report
            const bankData = {
                accountNumber: buildingData?.data?.accountNumber || buildingData?.accountNumber || buildingData?.AccountNumber || '',
                bankNumber: buildingData?.data?.bankNumber || buildingData?.bankNumber || buildingData?.BankNumber || '',
                bankName: buildingData?.data?.bankName || buildingData?.bankName || buildingData?.BankName || '',
                bankAccountName: buildingData?.data?.bankAccountName || buildingData?.bankAccountName || buildingData?.BankAccountName || ''
            };
            
            // Create the summary report using the utility function
            const message = createSummaryReport(unpaidApartments, buildingData, bankData);

            // Send WhatsApp message (no phone number = user chooses contact)
            const whatsappTab = sendWhatsAppMessage(message, null);

            // Show result to user
            if (!whatsappTab) {
                setReminderNotification({
                    type: 'error',
                    message: '❌ חלון WhatsApp נחסם על ידי הדפדפן. אנא אשר חלונות קופצים.'
                });
            } else {
                const totalUnpaidApartments = unpaidApartments.length;
                const totalDebt = unpaidApartments.reduce((sum, apt) =>
                    sum + (apt.totalDebt || (apt.monthsUnpaidCount * (apt.vaadMonthly || 500))), 0
                );

                setReminderNotification({
                    type: 'success',
                    message: `📱 נפתח WhatsApp עם דוח חובות מפורט של ${totalUnpaidApartments} דירות (סה"כ ₪${totalDebt.toLocaleString()})`
                });
            }

            setTimeout(() => setReminderNotification(null), 5000);

        } catch (error) {
            setReminderNotification({
                type: 'error',
                message: '❌ שגיאה ביצירת דוח WhatsApp'
            });
            setTimeout(() => setReminderNotification(null), 3000);
        }
    };

    // Send WhatsApp summary report with phone numbers
    const sendWhatsAppSummaryReportWithPhones = async () => {
        if (!unpaidApartments || unpaidApartments.length === 0) {
            setReminderNotification({
                type: 'success',
                message: '🎉 כל הדיירים שילמו! אין חובות לדווח עליהם'
            });
            setTimeout(() => setReminderNotification(null), 3000);
            return;
        }

        try {
            // Extract bank data from buildingData for the report
            const bankData = {
                accountNumber: buildingData?.data?.accountNumber || buildingData?.accountNumber || buildingData?.AccountNumber || '',
                bankNumber: buildingData?.data?.bankNumber || buildingData?.bankNumber || buildingData?.BankNumber || '',
                bankName: buildingData?.data?.bankName || buildingData?.bankName || buildingData?.BankName || '',
                bankAccountName: buildingData?.data?.bankAccountName || buildingData?.bankAccountName || buildingData?.BankAccountName || ''
            };
            
            // Create the summary report with phone numbers using the utility function
            const message = createSummaryReportWithPhones(unpaidApartments, buildingData, bankData);

            // Send WhatsApp message (no phone number = user chooses contact)
            const whatsappTab = sendWhatsAppMessage(message, null);

            // Show result to user
            if (!whatsappTab) {
                setReminderNotification({
                    type: 'error',
                    message: '❌ חלון WhatsApp נחסם על ידי הדפדפן. אנא אשר חלונות קופצים.'
                });
            } else {
                const totalUnpaidApartments = unpaidApartments.length;
                const totalDebt = unpaidApartments.reduce((sum, apt) =>
                    sum + (apt.totalDebt || (apt.monthsUnpaidCount * (apt.vaadMonthly || 500))), 0
                );
                const totalPhoneNumbers = unpaidApartments.reduce((sum, apt) =>
                    sum + apt.users.filter(user => user.phone).length, 0
                );

                setReminderNotification({
                    type: 'success',
                    message: `📱 נפתח WhatsApp עם דוח חובות כולל מספרי טלפון של ${totalUnpaidApartments} דירות (${totalPhoneNumbers} מספרי טלפון, סה"כ ₪${totalDebt.toLocaleString()})`
                });
            }

            setTimeout(() => setReminderNotification(null), 5000);

        } catch (error) {
            setReminderNotification({
                type: 'error',
                message: '❌ שגיאה ביצירת דוח WhatsApp עם מספרי טלפון'
            });
            setTimeout(() => setReminderNotification(null), 3000);
        }
    };

    // Handle WhatsApp dropdown positioning
    const handleWhatsappDropdownToggle = (event) => {
        if (!isWhatsappDropdownOpen) {
            const rect = event.currentTarget.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY + 8,
                left: rect.left + window.scrollX
            });
        }
        setIsWhatsappDropdownOpen(!isWhatsappDropdownOpen);
    };

    // Utility functions
    const getStatusClass = (status) => {
        switch (status) {
            case 'פתוח': return styles.statusInProgress;
            case 'הושלם': return styles.statusCompleted;
            case 'בטיפול': return styles.statusWaiting;
            default: return styles.statusDefault;
        }
    };

    const getUnpaidPriorityClass = (monthsCount) => {
        if (monthsCount >= 2) return styles.priorityHigh;
        if (monthsCount === 1) return styles.priorityMedium;
        return styles.priorityLow;
    };

    const getUnpaidPriorityText = (monthsCount) => {
        if (monthsCount === 0) return 'לא זמין';
        if (monthsCount === 1) return '1 חודש';
        return `${monthsCount} חודשים`;
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isWhatsappDropdownOpen && !event.target.closest(`.${styles.whatsappDropdown}`)) {
                setIsWhatsappDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isWhatsappDropdownOpen]);

    // Enhanced table rendering with double-click functionality
    const renderTable = (title, icon, data, columns, onViewAll, containerClass = '') => (
        <div className={`${styles.tableContainer} ${containerClass} ${isDashboardLoading ? styles.loading : ''}`}>
            <div className={styles.tableHeader}>
                <h2 className={styles.tableTitle}>
                    {icon}
                    <span>{title}</span>
                </h2>
                <button className={styles.viewAllButton} onClick={onViewAll}>
                    {title}
                    <ChevronLeft size={16} className={styles.buttonIcon} />
                </button>
            </div>
            <div className={styles.tableScroll}>
                <table className={styles.dataTable}>
                    <thead className={styles.tableThead}>
                        <tr>
                            {columns.map((column, index) => (
                                <th key={index} className={styles.tableTh}>
                                    {column.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className={styles.tableTbody}>
                        {data && data.length > 0 ? (
                            data.map((item, index) => (
                                <tr
                                    key={item.id || index}
                                    className={`${styles.tableRow} ${item.className || ''}`}
                                    onClick={item.onClick}
                                    onDoubleClick={item.onDoubleClick}
                                    style={item.onClick || item.onDoubleClick ? { cursor: 'pointer' } : {}}
                                    title={item.onDoubleClick ? 'לחץ פעמיים לעריכה' : item.title}
                                >
                                    {columns.map((column, columnIndex) => (
                                        <td key={columnIndex} className={`${styles.tableCell} ${column.cellClass || ''}`}>
                                            {column.render ? column.render(item) : item[column.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className={styles.tableCell}>
                                    {title === t('unpaidResidents') ? '🎉 כל הדיירים שילמו בזמן!' : `אין ${title} להצגה`}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    // Enhanced card rendering for mobile with double-click functionality
    const renderCards = (title, icon, data, CardComponent, onViewAll, containerClass = '') => (
        <div className={`${styles.cardContainer} ${containerClass} ${isDashboardLoading ? styles.loading : ''}`}>
            <div className={styles.cardContainerHeader}>
                <h2 className={styles.tableTitle}>
                    {icon}
                    <span>{title}</span>
                </h2>
                <button className={styles.viewAllButton} onClick={onViewAll}>
                    {title}
                    <ChevronLeft size={16} className={styles.buttonIcon} />
                </button>
            </div>
            <div className={styles.cardsGrid}>
                {data && data.length > 0 ? (
                    data.map((item, index) => (
                        <CardComponent key={item.id || index} {...item} />
                    ))
                ) : (
                    <div className={styles.emptyState}>
                        {title === t('unpaidResidents') ? '🎉 כל הדיירים שילמו בזמן!' : `אין ${title} להצגה`}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div>
            {/* Reminder Notification */}
            {reminderNotification && (
                <div className={`${styles.reminderNotification} ${reminderNotification.type}`}>
                    <div className={styles.notificationContent}>
                        <span>{reminderNotification.message}</span>
                        <button onClick={() => setReminderNotification(null)} className={styles.notificationClose}>
                            <X size={16} />
                        </button>
                    </div>
                </div>
            )}

            {/* Dashboard Header */}
            <div className={styles.dashboardHeader}>
                <div className={styles.dashboardTitleSection}>
                    {buildingData?.data?.createDate || buildingData?.createDate || buildingData?.CreatedDate ? (
                        <div
                            className={styles.buildingAge}
                            onClick={() => navigate('/app/settings', { state: { activeTab: 'editBuilding' } })}
                            style={{ cursor: 'pointer' }}
                            title="לחץ לעריכת פרטי הבניין"
                        >
                            <span className={styles.desktopOnly}>
                                📅 הבניין פעיל מאז: {safeDateFormat(buildingData?.data?.createDate || buildingData?.createDate || buildingData?.CreatedDate)}
                                {' '}({Math.ceil((new Date() - safeParseDate(buildingData?.data?.createDate || buildingData?.createDate || buildingData?.CreatedDate)) / (1000 * 60 * 60 * 24 * 30))} חודשים)
                            </span>
                            <span className={styles.mobileOnly}>
                                📍 {[
                                    buildingAddress?.buildingAddress || buildingAddress?.address,
                                    buildingAddress?.addressNumber,
                                    buildingAddress?.city
                                ].filter(Boolean).join(', ') || 'כתובת לא זמינה'}
                            </span>
                        </div>
                    ) : (buildingAddress?.buildingAddress || buildingAddress?.address || buildingAddress?.city) ? (
                        <div
                            className={styles.buildingAge}
                            onClick={() => navigate('/app/settings', { state: { activeTab: 'editBuilding' } })}
                            style={{ cursor: 'pointer' }}
                            title="לחץ לעריכת פרטי הבניין"
                        >
                            <span className={styles.mobileOnly}>
                                📍 {[
                                    buildingAddress?.buildingAddress || buildingAddress?.address,
                                    buildingAddress?.addressNumber,
                                    buildingAddress?.city
                                ].filter(Boolean).join(', ')}
                            </span>
                        </div>
                    ) : null}
                    {currentBankBalance !== null && (
                        <div
                            className={styles.currentBalance}
                            onClick={() => navigate('/app/bank-status')}
                            style={{ cursor: 'pointer' }}
                            title="לחץ לצפייה בסטטוס בנק"
                        >
                            💰 יתרה נוכחית: ₪{currentBankBalance.toLocaleString()}
                        </div>
                    )}
                </div>
            </div>

            {/* Loading indicator */}
            {isDashboardLoading && (
                <div className={styles.loadingIndicator}>
                    <p>טוען נתונים...</p>
                </div>
            )}

            {/* Stat Cards */}
            <StatCards
                usersLength={usersLength}
                currentMonthIncome={currentMonthIncome}
                currentMonthExpenses={currentMonthExpenses}
                currentMonthNetProfit={currentMonthNetProfit}
                tasksCount={tasksData?.tasks?.length || 0}
                isDashboardLoading={isDashboardLoading}
                t={t}
            />

            {/* Desktop Tables */}
            <div className={styles.tablesGrid}>
                {/* Tasks Table with Double-Click - FIXED */}
                {renderTable(
                    t('upcomingTasks'),
                    <CheckSquare size={20} className={styles.tableIcon} />,
                    tasksData?.tasks?.map(task => ({
                        id: task.tasksID,
                        description: task.description,
                        status: task.status,
                        date: safeDateFormat(task.date, 'en-GB'),
                        taskUser: task.taskUser,
                        onDoubleClick: () => handleTaskDoubleClick(task)
                    })) || [],
                    [
                        { header: t('description'), key: 'description', cellClass: styles.tableCellTitle },
                        {
                            header: t('status'),
                            key: 'status',
                            render: (item) => (
                                <span className={`${styles.statusBadge} ${getStatusClass(item.status)}`}>
                                    {item.status}
                                </span>
                            )
                        },
                        { header: t('dueDate'), key: 'date', cellClass: styles.tableCellDate },
                        { header: t('residentName'), key: 'taskUser', cellClass: styles.tableCellText }
                    ],
                    () => navigate('/app/tasks')
                )}

                {/* Events Table with Double-Click */}
                {renderTable(
                    t('upcomingEvents'),
                    <Calendar size={20} className={styles.tableIcon} />,
                    eventsData?.map(event => ({
                        id: event.id,
                        name: event.name,
                        date: event.date,
                        time: event.time,
                        location: event.location,
                        onDoubleClick: () => handleEventDoubleClick(event)
                    })) || [],
                    [
                        { header: t('events'), key: 'name', cellClass: styles.tableCellTitle },
                        { header: t('date'), key: 'date', cellClass: styles.tableCellDate },
                        { header: t('time'), key: 'time', cellClass: styles.tableCellText },
                        { header: t('location'), key: 'location', cellClass: styles.tableCellText }
                    ],
                    () => navigate('/app/events')
                )}

                {/* Unpaid Apartments Table with Updated WhatsApp Button */}
                <div className={`${styles.tableContainer} ${styles.alertsContainer} ${isDashboardLoading ? styles.loading : ''}`}>
                    <div className={styles.tableHeader}>
                        <h2 className={styles.tableTitle}>
                            <AlertTriangle size={20} className={styles.tableIcon} />
                            <span>{t('unpaidResidents')}</span>
                        </h2>
                        <div className={styles.tableHeaderButtons}>
                            <div className={`${styles.whatsappDropdown} ${styles.actionsDropdown}`}>
                                <button
                                    className={`${styles.refreshButton} ${styles.whatsappSummaryButton}`}
                                    onClick={handleWhatsappDropdownToggle}
                                    disabled={isDashboardLoading || !unpaidApartments || unpaidApartments.length === 0}
                                    title={`שלח דוח חובות בהודעת WhatsApp${unpaidApartments && unpaidApartments.length > 0 ? ` (${unpaidApartments.length} דירות חייבות)` : ''}`}
                                >
                                    <MessageSquare size={16} />
                                    <span>שליחה WhatsApp</span>
                                    <ChevronDown
                                        size={12}
                                        className={`${styles.chevronIcon} ${isWhatsappDropdownOpen ? styles.rotated : ''}`}
                                    />
                                    {unpaidApartments && unpaidApartments.length > 0 && (
                                        <span className={styles.unpaidCount}>{unpaidApartments.length}</span>
                                    )}
                                </button>

                                {isWhatsappDropdownOpen && (
                                    <div 
                                        className={styles.dropdownMenu}
                                        style={{
                                            top: `${dropdownPosition.top}px`,
                                            left: `${dropdownPosition.left}px`
                                        }}
                                    >
                                        <button
                                            className={styles.dropdownItem}
                                            onClick={() => {
                                                sendWhatsAppSummaryReport();
                                                setIsWhatsappDropdownOpen(false);
                                            }}
                                            disabled={isDashboardLoading}
                                        >
                                            <MessageSquare size={16} />
                                            <span>ללא מספרי טלפון</span>
                                        </button>
                                        
                                        <button
                                            className={styles.dropdownItem}
                                            onClick={() => {
                                                sendWhatsAppSummaryReportWithPhones();
                                                setIsWhatsappDropdownOpen(false);
                                            }}
                                            disabled={isDashboardLoading}
                                        >
                                            <MessageSquare size={16} />
                                            <span>כולל מספרי טלפון</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button className={styles.viewAllButton} onClick={() => navigate('/app/payments')}>
                                {t('unpaidResidents')}
                                <ChevronLeft size={16} className={styles.buttonIcon} />
                            </button>
                        </div>
                    </div>
                    <div className={styles.tableScroll}>
                        <table className={styles.dataTable}>
                            <thead className={styles.tableThead}>
                                <tr>
                                    <th className={styles.tableTh}>{t('residentName')}</th>
                                    <th className={styles.tableTh}>{t('apartmentNumber')}</th>
                                    <th className={styles.tableTh}>{t('phone')}</th>
                                    <th className={styles.tableTh}>{t('unpaidMonths')}</th>
                                </tr>
                            </thead>
                            <tbody className={styles.tableTbody}>
                                {unpaidApartments && unpaidApartments.length > 0 ? (
                                    unpaidApartments.map((apartment, index) => (
                                        <tr
                                            key={apartment.id}
                                            className={`${styles.tableRow} ${styles.unpaidUserRow}`}
                                            onClick={() => handleUnpaidApartmentClick(apartment)}
                                            style={{ cursor: 'pointer' }}
                                            title={t('clickForDetails')}
                                        >
                                            <td className={`${styles.tableCell} ${styles.tableCellTitle}`}>
                                                <div>
                                                    <div style={{ fontWeight: '700', fontSize: '1rem', marginBottom: '0.25rem' }}>
                                                        דירה {apartment.apartmentNumber}
                                                    </div>
                                                    <div style={{ fontSize: '0.875rem', color: '#1e293b' }}>
                                                        {apartment.users.slice(0, 2).map((user, index) => (
                                                            <div key={user.id} style={{ marginBottom: '0.125rem' }}>
                                                                {user.name}
                                                            </div>
                                                        ))}
                                                        {apartment.users.length > 2 && (
                                                            <div style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>
                                                                +{apartment.users.length - 2} דיירים נוספים
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className={`${styles.tableCell} ${styles.tableCellText}`}>
                                                <span style={{ fontWeight: '600' }}>{apartment.apartmentNumber}</span>
                                            </td>
                                            <td className={`${styles.tableCell} ${styles.tableCellText}`}>
                                                <div>
                                                    {apartment.users.filter(user => user.phone).slice(0, 2).map((user, index) => (
                                                        <div key={user.id} style={{ fontSize: '0.875rem', marginBottom: '0.125rem' }}>
                                                            {user.phone}
                                                        </div>
                                                    ))}
                                                    {apartment.users.filter(user => user.phone).length === 0 && (
                                                        <span style={{ color: '#94a3b8' }}>לא זמין</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className={styles.tableCell}>
                                                {(() => {
                                                    const buildingCreateDate = buildingData?.data?.createDate || buildingData?.createDate || buildingData?.CreatedDate;
                                                    let buildingAgeMonths = apartment.monthsUnpaidCount;

                                                    if (buildingCreateDate && buildingCreateDate !== 'לא זמין') {
                                                        const currentDate = new Date();
                                                        const createDate = safeParseDate(buildingCreateDate);
                                                        if (createDate) {
                                                            buildingAgeMonths = Math.ceil((currentDate - createDate) / (1000 * 60 * 60 * 24 * 30));
                                                        }
                                                    }

                                                    return (
                                                        <span
                                                            className={`${styles.priorityBadge} ${getUnpaidPriorityClass(apartment.monthsUnpaidCount)}`}
                                                            title={`חודשים לא שולמו: ${apartment.unpaidMonths?.join(', ') || 'לא זמין'} (מתוך ${buildingAgeMonths} חודשים מאז הקמת הבניין)`}
                                                        >
                                                            {getUnpaidPriorityText(apartment.monthsUnpaidCount)} / {buildingAgeMonths}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4} className={styles.tableCell}>
                                            🎉 כל הדיירים שילמו בזמן!
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Mobile Cards */}
            <div className={styles.mobileCardsContainer}>
                {/* Tasks Cards with Double-Click - FIXED */}
                {renderCards(
                    'משימות אחרונות',
                    <CheckSquare size={20} className={styles.tableIcon} />,
                    tasksData?.tasks?.map(task => ({
                        id: task.tasksID,
                        description: task.description,
                        status: task.status,
                        date: safeDateFormat(task.date, 'en-GB'),
                        taskUser: task.taskUser,
                        getStatusClass: getStatusClass,
                        onDoubleClick: () => handleTaskDoubleClick(task)
                    })) || [],
                    TaskCard,
                    () => navigate('/app/tasks')
                )}

                {/* Events Cards with Double-Click */}
                {renderCards(
                    'אירועים קרובים',
                    <Calendar size={20} className={styles.tableIcon} />,
                    eventsData?.map(event => ({
                        id: event.id,
                        name: event.name,
                        date: event.date,
                        time: event.time,
                        location: event.location,
                        onDoubleClick: () => handleEventDoubleClick(event)
                    })) || [],
                    EventCard,
                    () => navigate('/app/events')
                )}

                {/* Unpaid Apartments Cards with Updated WhatsApp Button */}
                <div className={`${styles.cardContainer} ${styles.alertsContainer} ${isDashboardLoading ? styles.loading : ''}`}>
                    <div className={styles.cardContainerHeader}>
                        <h2 className={styles.tableTitle}>
                            <AlertTriangle size={20} className={styles.tableIcon} />
                            <span>{t('unpaidResidents')}</span>
                        </h2>
                        <div className={styles.tableHeaderButtons}>
                            <div className={`${styles.whatsappDropdown} ${styles.actionsDropdown}`}>
                                <button
                                    className={`${styles.refreshButton} ${styles.whatsappSummaryButton} ${styles.mobileWhatsappButton}`}
                                    onClick={handleWhatsappDropdownToggle}
                                    disabled={isDashboardLoading || !unpaidApartments || unpaidApartments.length === 0}
                                    title={`שלח דוח חובות בהודעת WhatsApp${unpaidApartments && unpaidApartments.length > 0 ? ` (${unpaidApartments.length} דירות חייבות)` : ''}`}
                                >
                                    <MessageSquare size={14} />
                                    <span>שליחה WhatsApp</span>
                                    <ChevronDown
                                        size={10}
                                        className={`${styles.chevronIcon} ${isWhatsappDropdownOpen ? styles.rotated : ''}`}
                                    />
                                    {unpaidApartments && unpaidApartments.length > 0 && (
                                        <span className={styles.unpaidCount}>{unpaidApartments.length}</span>
                                    )}
                                </button>

                                {isWhatsappDropdownOpen && (
                                    <div 
                                        className={styles.dropdownMenu}
                                        style={{
                                            top: `${dropdownPosition.top}px`,
                                            left: `${dropdownPosition.left}px`
                                        }}
                                    >
                                        <button
                                            className={styles.dropdownItem}
                                            onClick={() => {
                                                sendWhatsAppSummaryReport();
                                                setIsWhatsappDropdownOpen(false);
                                            }}
                                            disabled={isDashboardLoading}
                                        >
                                            <MessageSquare size={14} />
                                            <span>ללא מספרי טלפון</span>
                                        </button>
                                        
                                        <button
                                            className={styles.dropdownItem}
                                            onClick={() => {
                                                sendWhatsAppSummaryReportWithPhones();
                                                setIsWhatsappDropdownOpen(false);
                                            }}
                                            disabled={isDashboardLoading}
                                        >
                                            <MessageSquare size={14} />
                                            <span>כולל מספרי טלפון</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button className={styles.viewAllButton} onClick={() => navigate('/app/payments')}>
                                {t('unpaidResidents')}
                                <ChevronLeft size={16} className={styles.buttonIcon} />
                            </button>
                        </div>
                    </div>
                    <div className={styles.cardsGrid}>
                        {unpaidApartments && unpaidApartments.length > 0 ? (
                            unpaidApartments.map((apartment, index) => (
                                <UnpaidApartmentCard
                                    key={apartment.id}
                                    apartmentNumber={apartment.apartmentNumber}
                                    users={apartment.users}
                                    usersCount={apartment.usersCount}
                                    monthsUnpaidCount={apartment.monthsUnpaidCount}
                                    buildingCreateDate={buildingData?.data?.createDate || buildingData?.createDate || buildingData?.CreatedDate || 'לא זמין'}
                                    getUnpaidPriorityClass={getUnpaidPriorityClass}
                                    getUnpaidPriorityText={getUnpaidPriorityText}
                                    onClick={() => handleUnpaidApartmentClick(apartment)}
                                />
                            ))
                        ) : (
                            <div className={styles.emptyState}>
                                🎉 כל הדיירים שילמו בזמן!
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Unpaid Details Modal */}
            <UnpaidDetailsModal
                apartmentData={selectedUnpaidApartment}
                isOpen={isUnpaidModalOpen}
                onClose={() => setIsUnpaidModalOpen(false)}
                onSendReminder={handleReminderSent}
                buildingData={buildingData}
            />
            
            {/* Recent Backups Modal - Admin Only */}
            {showBackupModal && (
                <div className={styles.backupModalOverlay} onClick={() => setShowBackupModal(false)}>
                    <div className={styles.backupModalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.backupModalHeader}>
                            <h3 className={styles.backupModalTitle}>
                                <FileText size={20} />
                                <span>גיבויים אחרונים (7 ימים)</span>
                            </h3>
                            <button className={styles.backupModalClose} onClick={() => setShowBackupModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className={styles.backupModalBody}>
                            {isBackupsLoading ? (
                                <div className={styles.backupListLoading}>טוען גיבויים...</div>
                            ) : recentBackups.length > 0 ? (
                                <div className={styles.backupListTable}>
                                    <div className={styles.backupCount}>{recentBackups.length} גיבויים</div>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>שם קובץ</th>
                                                <th>תאריך</th>
                                                <th>גודל</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentBackups.map((backup, index) => (
                                                <tr key={index}>
                                                    <td>{backup.fileName}</td>
                                                    <td>{new Date(backup.createdAt).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                                                    <td>{backup.fileSizeMB} MB</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className={styles.backupListEmpty}>אין גיבויים ב-7 ימים האחרונים</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

             {/* Recurring Expenses Popup */}
            <RecurringExpensesPopup
                expenses={recurringExpenses}
                isOpen={showRecurringPopup}
                onClose={closeRecurringPopup}
                onExpensesCreated={handleRecurringExpenseCreated}
                buildingId={userData?.buildingId}
            />

        </div>
    );
}